import React, { useEffect, useRef, useState } from 'react';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import axios from 'axios';
import 'xterm/css/xterm.css';
import { createWebSocketWithFailover } from '../utils/serverFailover';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function RealTerminal({ containerId, questId, questTitle, levelData, currentLevel, totalLevels, studentId, hostPort, isPersistent, onExit, onLevelComplete }) {
  const terminalRef = useRef(null);
  const xtermRef = useRef(null);
  const wsRef = useRef(null);
  const fitAddonRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const [connected, setConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [error, setError] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [showHint, setShowHint] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('Inicializando terminal...');
  const MAX_RECONNECT_ATTEMPTS = 5;
  const RECONNECT_DELAY = 2000; // 2 seconds
  const keepAliveRef = useRef(null);
  const serverAssignedRef = useRef(null);
  const mountedRef = useRef(true);

	// Function to connect/reconnect to WebSocket with failover
	const connectWebSocket = async (term) => {
	  if (!term || !containerId) return;

	  try {
		setConnectionStatus('Conectando terminal...');
		const ws = await createWebSocketWithFailover(
		  containerId,
		  studentId,
		  (connectedWs, server) => {
			// Connection successful
			wsRef.current = connectedWs;
			serverAssignedRef.current = server;
			setConnected(true);
			setReconnecting(false);
			reconnectAttemptsRef.current = 0;
			setConnectionStatus(`Terminal conectado a ${server.name}`);
			
			// Keepalive ping every 30 seconds
			keepAliveRef.current = setInterval(() => {
			  if (connectedWs.readyState === WebSocket.OPEN) {
				connectedWs.send(''); // Send null byte as keepalive
			  }
			}, 30000);
			
			if (term) {
			  // Clear terminal and reset cursor
			  term.reset();
			  
			  // Send clear screen command to container
			  if (connectedWs.readyState === WebSocket.OPEN) {
				connectedWs.send('\x0c'); // Form feed (clear screen)
			  }
			  
			  term.writeln(`\x1b[32m✅ Conectado a terminal Linux real (${server.name})\x1b[0m`);
			  if (reconnectAttemptsRef.current > 0) {
				term.writeln('\x1b[32m🔄 Reconexión exitosa\x1b[0m');
			  }
			  term.writeln('\x1b[36m💡 Todos los comandos de Linux están disponibles\x1b[0m');
			  term.writeln('');
			  
			  // Force terminal refresh
			  setTimeout(() => {
				if (fitAddonRef.current && mountedRef.current) {
				  fitAddonRef.current.fit();
				  term.refresh(0, term.rows - 1);
				}
			  }, 100);
			}
		  },
		  (event) => {
			// onMessage handler
			if (!term) return;
			if (typeof event.data === 'string') {
			  term.write(event.data);
			} else {
			  event.data.arrayBuffer().then(buffer => {
				const uint8Array = new Uint8Array(buffer);
				if (term) {
				  term.write(uint8Array);
				}
			  });
			}
		  },
		  (error) => {
			// onError handler
			console.error('WebSocket error:', error);
			setError('Error de conexión con el terminal');
			setConnectionStatus('Error de conexión');
		  },
		  (event) => {
			// onClose handler
			setConnected(false);
			setConnectionStatus('Terminal desconectado');
			
			// Clear keepalive
			if (keepAliveRef.current) {
			  clearInterval(keepAliveRef.current);
			  keepAliveRef.current = null;
			}
			
			console.log('WebSocket closed:', event.code, event.reason);
		  },
		  (status) => {
			// Status update handler
			setConnectionStatus(status);
			if (term && status.includes('⚠️')) {
			  term.writeln(`\x1b[33m${status}\x1b[0m`);
			}
		  }
		);

		// Setup data handler
		const dataHandler = (data) => {
		  if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
			wsRef.current.send(data);
		  }
		};
		term.onData(dataHandler);

		return ws;
	  } catch (error) {
		console.error('Failed to connect WebSocket:', error);
		setError(error.message);
		setConnectionStatus('❌ No se pudo conectar el terminal');
		if (term) {
		  term.writeln(`\x1b[31m❌ ${error.message}\x1b[0m`);
		  term.writeln('\x1b[33m💡 Intenta recargar la página\x1b[0m');
		}
	  }
	};

  useEffect(() => {
    if (!containerId || !terminalRef.current) return;

    mountedRef.current = true;
    let term = null;
    let fitAddon = null;

    // Small delay to ensure DOM is ready
    const initTerminal = () => {
      if (!mountedRef.current || !terminalRef.current) return;

      try {
        // Create terminal
        term = new Terminal({
          cursorBlink: true,
          fontSize: 14,
          fontFamily: 'Menlo, Monaco, "Courier New", monospace',
          theme: {
            background: '#1e1e1e',
            foreground: '#cccccc',
            cursor: '#00ff00',
            black: '#000000',
            red: '#cd3131',
            green: '#0dbc79',
            yellow: '#e5e510',
            blue: '#2472c8',
            magenta: '#bc3fbc',
            cyan: '#11a8cd',
            white: '#e5e5e5',
            brightBlack: '#666666',
            brightRed: '#f14c4c',
            brightGreen: '#23d18b',
            brightYellow: '#f5f543',
            brightBlue: '#3b8eea',
            brightMagenta: '#d670d6',
            brightCyan: '#29b8db',
            brightWhite: '#ffffff'
          }
        });

        fitAddon = new FitAddon();
        term.loadAddon(fitAddon);
        term.open(terminalRef.current);
        
        // Wait for terminal to be fully rendered before fitting
        setTimeout(() => {
          if (mountedRef.current && fitAddon) {
            fitAddon.fit();
          }
        }, 100);

        xtermRef.current = term;
        fitAddonRef.current = fitAddon;

        // Connect to WebSocket with auto-reconnect
        connectWebSocket(term);

        // Check for web URL availability

        // Handle window resize
        const handleResize = () => {
          if (fitAddon && mountedRef.current) {
            try {
              fitAddon.fit();
            } catch (e) {
              console.error('Error fitting terminal:', e);
            }
          }
        };
        window.addEventListener('resize', handleResize);

        // Store cleanup function
        return () => {
          mountedRef.current = false;
          window.removeEventListener('resize', handleResize);
          
          // Clear reconnect timeout
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
          }
          
          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.close();
          }
          if (term) {
            term.dispose();
          }
        };
      } catch (error) {
        console.error('Error initializing terminal:', error);
        setError('Error al inicializar el terminal');
      }
    };

    // Initialize with small delay
    const timer = setTimeout(initTerminal, 100);

    return () => {
      clearTimeout(timer);
      // Clean up reconnect timeout on unmount
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [containerId]);


  const handlePasswordSubmit = async () => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/linux-quest/${questId}/validate-password`, {
        current_level: currentLevel,
        password: passwordInput,
        student_id: studentId
      });

      if (response.data.correct) {
        if (response.data.completed) {
          alert('🎉 ¡Felicitaciones! Has completado el Terminal Quest completo!');
          onExit();
        } else {
          alert(`✅ ${response.data.message}\n\n¡Nivel ${currentLevel} completado! Recargando para el nivel ${response.data.next_level}...`);
          // Notify parent to reload with new level
          if (onLevelComplete) {
            onLevelComplete(response.data.next_level);
          }
          setPasswordInput('');
          setShowPasswordModal(false);
        }
      } else {
        alert(`❌ ${response.data.message}`);
        setPasswordInput('');
      }
    } catch (error) {
      console.error('Error validating password:', error);
      alert('Error al validar el password');
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="bg-gray-800 rounded-t-xl p-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-white">{questTitle}</h1>
            <p className="text-sm text-gray-400">Nivel {currentLevel} de {totalLevels}</p>
            {connected && !reconnecting && (
              <span className="inline-flex items-center mt-2 px-3 py-1 bg-green-600 text-white text-xs font-semibold rounded-full">
                <span className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse"></span>
                Terminal Real Activo
              </span>
            )}
            {reconnecting && (
              <span className="inline-flex items-center mt-2 px-3 py-1 bg-yellow-600 text-white text-xs font-semibold rounded-full">
                <span className="w-2 h-2 bg-white rounded-full mr-2 animate-spin"></span>
                Reconectando... ({reconnectAttemptsRef.current}/{MAX_RECONNECT_ATTEMPTS})
              </span>
            )}
            {!connected && !reconnecting && (
              <span className="inline-flex items-center mt-2 px-3 py-1 bg-gray-600 text-white text-xs font-semibold rounded-full">
                Desconectado
              </span>
            )}
            {error && (
              <span className="inline-flex items-center mt-2 px-3 py-1 bg-red-600 text-white text-xs font-semibold rounded-full">
                {error}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            {hostPort && (
              <button
                onClick={() => {
                  const protocol = window.location.protocol;
                  const serverHost = window.location.hostname;
                  const proxyUrl = `https://terminal-${hostPort}.play.leaderdreams.com/`;
                  window.open(proxyUrl, '_blank');
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm"
              >
                🌐 Abrir en Navegador
              </button>
            )}
            {levelData?.hint && (
              <button
                onClick={() => setShowHint(!showHint)}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition text-sm"
              >
                💡 {showHint ? 'Ocultar' : 'Ver'} Pista
              </button>
            )}
            <button
              onClick={() => setShowPasswordModal(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm"
            >
              🔑 Ingresar Password
            </button>
            <button
              onClick={() => {
                if (xtermRef.current) {
                  xtermRef.current.reset();
                  if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                    wsRef.current.send('\x0c'); // Clear screen
                  }
                  setTimeout(() => {
                    if (fitAddonRef.current) {
                      fitAddonRef.current.fit();
                      xtermRef.current.refresh(0, xtermRef.current.rows - 1);
                    }
                  }, 50);
                }
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm"
              title="Limpiar terminal (Ctrl+L)"
            >
              🔄 Limpiar
            </button>
            {isPersistent ? (
              <>
                <button
                  onClick={() => onExit(false)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition text-sm"
                  title="Guardar el contenedor para continuar después"
                >
                  💾 Guardar y Salir
                </button>
                <button
                  onClick={() => onExit(true)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm"
                  title="Eliminar el contenedor permanentemente"
                >
                  🗑️ Salir sin Guardar
                </button>
              </>
            ) : (
              <button
                onClick={onExit}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition text-sm"
              >
                ← Salir
              </button>
            )}
          </div>
        </div>

        {/* Level Description */}
        <div className="bg-gray-800 border-t border-gray-700 p-4">
          <h3 className="text-white font-semibold mb-2">📋 Objetivo:</h3>
          <p className="text-gray-300 text-sm">{levelData?.description || 'Completa el desafío de este nivel'}</p>
        </div>

        {/* Hint Panel */}
        {showHint && levelData?.hint && (
          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4">
            <p className="text-sm text-yellow-800">
              <strong>💡 Pista:</strong> {levelData.hint}
            </p>
          </div>
        )}

        {/* Terminal */}
        <div 
          ref={terminalRef} 
          className="bg-black rounded-b-xl p-4"
          style={{ height: '600px' }}
        />

        {/* Info */}
        <div className="bg-gray-800 rounded-lg p-4 mt-4 text-gray-300 text-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="font-semibold mb-2">💻 Comandos disponibles:</p>
              <ul className="space-y-1 text-xs">
                <li>• <code className="bg-gray-900 px-2 py-1 rounded">ls</code> - Listar archivos</li>
                <li>• <code className="bg-gray-900 px-2 py-1 rounded">cat archivo</code> - Ver contenido</li>
                <li>• <code className="bg-gray-900 px-2 py-1 rounded">grep texto archivo</code><code className="bg-gray-900 px-2 py-1 rounded"> grep -r "texto" .</code> - Buscar texto en archivo o recursivamente</li>
				<li>• <code className="bg-gray-900 px-2 py-1 rounded">find . -name "nombre_a_buscar"</code> - Busca archivos </li>
                <li>• <code className="bg-gray-900 px-2 py-1 rounded">mkdir carpeta</code> - Crear carpeta</li>
                <li>• <code className="bg-gray-900 px-2 py-1 rounded">rm archivo</code> - Eliminar archivo</li>
                <li>• <code className="bg-gray-900 px-2 py-1 rounded">pwd</code> - Directorio actual</li>
                <li>• <code className="bg-gray-900 px-2 py-1 rounded">cd carpeta</code> - Cambiar directorio</li>
                <li>• Y muchos más comandos Linux...</li>
              </ul>
            </div>
            <div>
			  <p className="font-semibold mb-2">¡¡Importante!!</p>
			  <p className="text-xs mb-2">En ubuntu comienza con: <code className="bg-gray-900 px-2 py-1 rounded">bash -l</code></p>
			  <p className="text-xs mb-2">En Alpine el gestor de paquetes es: <code className="bg-gray-900 px-2 py-1 rounded">apk add</code></p>
              <p className="font-semibold mb-2">📁 Ubicación de archivos:</p>
              <p className="text-xs mb-2">Los archivos del nivel están en: <code className="bg-gray-900 px-2 py-1 rounded">/root/</code></p>
              <p className="text-xs">Usa <code className="bg-gray-900 px-2 py-1 rounded">cd /root</code> para ir a esa ubicación</p>
            </div>
          </div>
        </div>
      </div>

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-800 mb-4">🔑 Ingresar Password del Nivel {currentLevel + 1}</h3>
            <p className="text-sm text-gray-600 mb-4">
              Si encontraste el password en este nivel, ingrésalo aquí para desbloquear el siguiente.
            </p>
            <input
              type="text"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4 font-mono"
              placeholder="nivel2_abc123"
              onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={handlePasswordSubmit}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-semibold"
              >
                Validar
              </button>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordInput('');
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RealTerminal;
